import {
  createElement,
  PropsWithChildren,
  ReactElement,
  useRef,
  useState,
} from 'react';
import { ReactQuestionFactory, ReactElementFactory } from 'survey-react-ui';
import { useAsyncFn } from 'react-use';
import { noop } from 'lodash';
import { PartialDeep } from 'type-fest';
import { IonButton, IonButtons, IonIcon } from '@ionic/react';
import { SvgRegistry } from 'survey-core';
import { StylesManager } from 'survey-core';

import { ICONS_BASE64 } from '../Icon';
import { Page } from '../Page';
import { Header } from '../Header';
import { Content } from '../Content';
import { Spinner } from '../Spinner';
import folderOpenIcon from '../Icon/svg/folder-open.svg?raw';
import trashIcon from '../Icon/svg/trash.svg?raw';
import { SurveyText } from '../../../src-v2/components/Survey/SurveyText';

import { SurveyFile } from './SurveyFile/SurveyFile';
import { SurveyQuestion } from './SurveyQuestion/SurveyQuestion';
import { SurveyNavButton } from './SurveyNavButton/SurveyNavButton';
import { SurvayTagbox } from './SurveyTagbox/SurveyTagbox';
import { SurveyComment } from './SurveyComment/SurveyComment';
import { SurveyDropdown } from './SurveyDropdown/SurveyDropdown';
import { SurveyProgressButtons } from './SurveyProgressButtons/SurveyProgressButtons';
import { SurveyPanel } from './SurveyPanel/SurveyPanel';
import { SurveyBoolean } from './SurveyBoolean/SurveyBoolean';
import { SurveyActionBar } from './SurveyActionBar/SurveyActionBar';
import { SurveyCheckbox } from './SurveyCheckbox/SurveyCheckbox';
import { SurveyCheckboxItem } from './SurveyCheckboxItem/SurveyCheckboxItem';
import { SurveyRadioGroup } from './SurveyRadioGroup/SurveyRadioGroup';
import { SurveyRadioGroupItem } from './SurveyRadioGroupItem/SurveyRadioGroupItem';
import { SurveyFilePreview } from './SurveyFilePreview/SurveyFilePreview';
import { SurveyContext } from './Survey.context';
import { SurveySurvey, SurveySurveyInstance } from './SurveySurvey';
import { defaultOnComplete, defaultOnNext } from './Survey.utils';
import {
  SurveyOnCompleteCallback,
  SurveyOnNextCallback,
  SurveyState,
  SurveyOnPageChangeCallback,
  SurveyOnUploadFilesCallback,
} from './Survey.types';

import styles from './Survey.module.scss';

StylesManager.applyTheme('defaultV2');

ReactQuestionFactory.Instance.registerQuestion('text', (props) =>
  createElement(SurveyText, props)
);

ReactQuestionFactory.Instance.registerQuestion('file', (props) =>
  createElement(SurveyFile, props)
);

ReactQuestionFactory.Instance.registerQuestion('tagbox', (props) =>
  createElement(SurvayTagbox, props)
);

ReactQuestionFactory.Instance.registerQuestion('comment', (props) =>
  createElement(SurveyComment, props)
);

ReactQuestionFactory.Instance.registerQuestion('dropdown', (props) =>
  createElement(SurveyDropdown, props)
);

ReactQuestionFactory.Instance.registerQuestion('boolean', (props) =>
  createElement(SurveyBoolean, props)
);

ReactQuestionFactory.Instance.registerQuestion('checkbox', (props) =>
  createElement(SurveyCheckbox, props)
);

ReactQuestionFactory.Instance.registerQuestion('radiogroup', (props) =>
  createElement(SurveyRadioGroup, props)
);

ReactElementFactory.Instance.registerElement('question', (props) =>
  createElement(SurveyQuestion, props)
);

ReactElementFactory.Instance.registerElement('panel', (props) =>
  createElement(SurveyPanel, props)
);

ReactElementFactory.Instance.registerElement('sv-nav-btn', (props) =>
  createElement(SurveyNavButton, props)
);

ReactElementFactory.Instance.registerElement('sv-progress-buttons', (props) =>
  createElement(SurveyProgressButtons, props)
);

ReactElementFactory.Instance.registerElement('sv-action-bar', (props) =>
  createElement(SurveyActionBar, props)
);

ReactElementFactory.Instance.registerElement('sv-file-preview', (props) =>
  createElement(SurveyFilePreview, props)
);

ReactElementFactory.Instance.registerElement('survey-checkbox-item', (props) =>
  createElement(SurveyCheckboxItem, props)
);

ReactElementFactory.Instance.registerElement(
  'survey-radiogroup-item',
  (props) => createElement(SurveyRadioGroupItem, props)
);

SvgRegistry.registerIconFromSvg('icon-choosefile', folderOpenIcon);
SvgRegistry.registerIconFromSvg('icon-clear', trashIcon);

export type SurveyProps<T extends object> = PropsWithChildren<{
  loading?: boolean;
  json: object;
  defaultBackHref?: string;
  onComplete?: SurveyOnCompleteCallback<T>;
  onNext?: SurveyOnNextCallback<T>;
  optionsImages?: Record<string, Record<string, string>>;
  initialValues?: PartialDeep<T>;
  onPageChange?: SurveyOnPageChangeCallback<T>;
  onUploadFiles: SurveyOnUploadFilesCallback;
}>;

interface SurveyComponent {
  <T extends object = {}>(props: SurveyProps<T>): ReactElement;
}

export const Survey: SurveyComponent = ({
  json,
  loading = false,
  defaultBackHref = '/',
  onComplete = defaultOnComplete,
  onNext = defaultOnNext,
  optionsImages = {},
  initialValues,
  onPageChange = noop,
  onUploadFiles = noop,
}) => {
  const survey = useRef<SurveySurveyInstance>();

  const [{ currentPageNo }, setState] = useState<SurveyState>({
    currentPageNo: 0,
  });

  const [{ loading: isCompleteInProgress }, dispatchOnComplete] = useAsyncFn<
    SurveyOnCompleteCallback<any>
  >(
    async (params) => {
      await onComplete(params);
    },
    [onComplete]
  );

  const [{ loading: isNextInProgress }, dispatchOnNext] = useAsyncFn<
    SurveyOnNextCallback<any>
  >(
    async (params) => {
      await onNext(params);
    },
    [onNext]
  );

  const completing = isCompleteInProgress || isNextInProgress;

  const handleOnBack = () => {
    survey?.current?.prevPage();
  };

  return (
    <Page headerBackground="periwinkle300">
      <Header>
        {currentPageNo === 0 ? (
          <Header.Back defaultHref={defaultBackHref} />
        ) : (
          <IonButtons>
            <IonButton onClick={handleOnBack}>
              <IonIcon slot="icon-only" icon={ICONS_BASE64['arrow-left']} />
            </IonButton>
          </IonButtons>
        )}
      </Header>
      {loading ? (
        <Content>
          <div className={styles.spinner}>
            <Spinner />
          </div>
        </Content>
      ) : (
        <SurveyContext.Provider value={{ optionsImages, completing }}>
          <SurveySurvey
            ref={survey}
            onUploadFiles={onUploadFiles}
            onPageChange={onPageChange}
            setState={setState}
            initialValues={initialValues}
            onComplete={dispatchOnComplete}
            onNext={dispatchOnNext}
            json={json}
          />
        </SurveyContext.Provider>
      )}
    </Page>
  );
};
